import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Typography, Box, Avatar, Grid } from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import format from 'date-fns/format';
import addDays from 'date-fns/addDays';

import { usePrevious } from 'shared/hooks';
import { Loader } from 'ui';
import { shortDateFormat } from 'shared/constants/dateFormat';

import { getProductById } from '../../selectors/shopSelectors';

const useStyles = makeStyles(theme => ({
  avatar: {
    width: 200,
    height: 200,
    backgroundColor: theme.palette.primary.contrastText,
    boxShadow: '0 4px 4px 3px rgba(155, 155, 155, 0.3)',
    margin: '0 auto',
  },
  lockOpenIcon: {
    fontSize: 100,
    color: '#0cd4a4',
    fontWeight: 'bold',
  },
}));

const ShopProductFreeSubscriptionModal = ({ open, onClose, onConfirm, data }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const startDate = new Date();
  const endDate = addDays(startDate, data.validityDays - data.cancellationPeriod);

  const watchedProduct = useSelector(getProductById(data.id));

  const [isSuccess, setIsSuccess] = useState(data.purchased);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = () => {
    setIsLoading(true);
    onConfirm();
  };

  const isPurchased = usePrevious(data.purchased);

  useEffect(() => {
    if (!isPurchased && watchedProduct.purchased) {
      setIsSuccess(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedProduct.purchased]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <Typography variant="h4">{data.name}</Typography>
      </DialogTitle>
      <DialogContent>
        {isSuccess ? (
          <Box width={500} height={500} py={12}>
            <Grid container alignContent="center" justifyContent="center">
              <Grid item>
                <Avatar className={classes.avatar}>
                  <LockOpenIcon className={classes.lockOpenIcon} />
                </Avatar>
                <Box mt={4}>
                  <Typography variant="h6">{t('Your subscription is active')}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <>
            <Box>
              <Typography>
                {t(
                  'We are delighted about your interest in our offering. Please click “Confirm” to start the subscription or “Cancel” to return to the product page. ',
                )}
              </Typography>
            </Box>

            <Box mt={4}>
              <Typography>
                {t('Subscription:')} <strong>{data.name}</strong>
              </Typography>
              <Typography>
                {t('Start:')} <strong>{format(startDate, shortDateFormat)}</strong>
              </Typography>
              <Typography>
                {t('Cancel by:')} <strong>{format(endDate, shortDateFormat)}</strong>
              </Typography>
              <Typography>
                {t('Price:')} <strong>{t('Free of charge')}</strong>
              </Typography>
            </Box>

            <Box mt={4}>
              <Typography>
                {t(
                  'The subscription provides full access to the content described in the product description for you and your organization.',
                )}
              </Typography>
            </Box>
            <Box mt={4}>
              <Typography>
                {t(
                  'You may cancel the subscription by visiting “My Purchases” in the subscription center. Please select the subscription and chose “Cancel” from the drop-down menu.',
                )}
              </Typography>
            </Box>
            <Box mt={4}>
              <Typography>
                {t(
                  'Because the subscription is free of charge, no further action is required from your side.',
                )}
              </Typography>
            </Box>
            <Box mt={4}>
              <Typography>
                {t(
                  'If you have any further questions, please contact your personal advisor or the Service Center.',
                )}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isSuccess ? (
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Button color="primary" variant="contained" onClick={onClose}>
                {t('Close')}
              </Button>
            </Grid>
          </Grid>
        ) : (
          <>
            <Button onClick={onClose} color="primary" variant="outlined">
              {t('Cancel')}
            </Button>
            {isLoading ? (
              <Box px={2}>
                <Loader />
              </Box>
            ) : (
              <Button onClick={handleConfirm} color="primary" variant="contained" autoFocus>
                {t('Confirm')}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

ShopProductFreeSubscriptionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    validityDays: PropTypes.number.isRequired,
    cancellationPeriod: PropTypes.number.isRequired,
    purchased: PropTypes.bool.isRequired,
  }).isRequired,
};

export { ShopProductFreeSubscriptionModal };
